import { 
  collection,
  addDoc,
  query,
  where,
  getDocs,
  getCountFromServer,
  orderBy,
  limit,
  serverTimestamp 
} from 'firebase/firestore'
import { db } from '../firebase'
import { getUserById, incrementUserXP } from './userService'
import { formatChordDetailed, userAnswerToLabel } from '../utils/chordFormatter'
import { computeSingleMarkerValidation, getImplicitBorrowedLocalKeyForMarker } from '../utils/exerciseMarkerValidation'
import { recordAttemptStats } from './statsService'

/** Remplace récursivement undefined par null (Firebase n'accepte pas undefined). */
function sanitizeForFirestore(value) {
  if (value === undefined) return null
  if (value === null) return null
  if (Array.isArray(value)) return value.map(sanitizeForFirestore)
  if (typeof value === 'object' && value !== null) {
    const out = {}
    for (const key of Object.keys(value)) {
      out[key] = sanitizeForFirestore(value[key])
    }
    return out
  }
  return value
}

/**
 * Meilleur score enregistré pour (userId, exerciseId), hors session live en cours si fournie.
 */
export async function getBestScoreForUserExercise(userId, exerciseId, { liveSessionId = null } = {}) {
  if (!userId || !exerciseId) return null
  try {
    const q = query(
      collection(db, 'attempts'),
      where('userId', '==', userId),
      where('exerciseId', '==', exerciseId),
      limit(50)
    )
    const snapshot = await getDocs(q)
    let best = null
    snapshot.forEach((docSnap) => {
      const data = docSnap.data()
      if (liveSessionId && data.liveSessionId === liveSessionId) return
      const score = data.score ?? 0
      if (best == null || score > best) best = score
    })
    return best
  } catch (error) {
    console.error('Erreur lors de la récupération du record exercice:', error)
    return null
  }
}

/**
 * Sauvegarde une tentative d'exercice
 * @param {Object} options - Options (functionOnlyAvailable, exerciseMetadata: { composer, difficulty, autoTags, sourceNodeId })
 */
export async function saveAttempt(userId, exerciseId, userAnswers, correctAnswers, score, exerciseTitle = null, options = {}) {
  try {
    const liveSessionId = options.liveSessionId != null ? String(options.liveSessionId) : null
    // Idempotence session live : un seul rendu par élève/exercice/session
    if (liveSessionId) {
      const existingQ = query(
        collection(db, 'attempts'),
        where('liveSessionId', '==', liveSessionId),
        where('exerciseId', '==', exerciseId),
        where('userId', '==', userId),
        limit(1)
      )
      const existingSnap = await getDocs(existingQ)
      if (!existingSnap.empty) {
        const existing = existingSnap.docs[0]
        return {
          id: existing.id,
          ...existing.data(),
          xpGained: 0,
          skippedDuplicate: true
        }
      }
    }

    // Calculer l'XP par marqueur selon le niveau de validation (réponses partielles = XP partielle)
    const XP_BY_LEVEL = { 1: 10, 2: 6, 3: 4, 0.5: 5, 0: 0 }
    let xpGained = 0
    let correctCount = 0
    const progressionMode = options.progressionMode ?? (options.functionOnlyAvailable ? 'functions' : 'full')
    const exerciseMode = options.exerciseMode || 'libre'
    const parcoursContext = options.parcoursContext ?? null
    const markers = options.exerciseMarkers
    correctAnswers.forEach((correct, index) => {
      const userAnswer = userAnswers[index]
      if (!correct) return
      if (!userAnswer) return
      const implicitBor =
        Array.isArray(markers) && markers.length > 0
          ? getImplicitBorrowedLocalKeyForMarker(markers, index, correctAnswers, userAnswers)
          : null
      const validation = computeSingleMarkerValidation({
        userAnswer,
        correctAnswer: correct,
        index,
        progressionMode,
        exerciseMode,
        parcoursContext,
        cachedValidation: null,
        isLocked: false,
        tonality: options.tonality || 'C',
        dominantMode: options.mode || 'major',
        implicitBorrowedFromDegree: implicitBor
      })
      if (!validation) return
      const level = validation?.level ?? 0
      xpGained += XP_BY_LEVEL[level] ?? 0
      if (level === 1) correctCount += 1
    })

    const priorBestScore =
      options.priorBestScore != null
        ? Number(options.priorBestScore)
        : await getBestScoreForUserExercise(userId, exerciseId, { liveSessionId })
    let noXpReason = null
    if (priorBestScore != null && score <= priorBestScore) {
      xpGained = 0
      noXpReason = 'record_unchanged'
    }
    
    const meta = options.exerciseMetadata || {}
    const attemptData = {
      userId,
      exerciseId,
      exerciseTitle: exerciseTitle ?? null,
      userAnswers: sanitizeForFirestore(userAnswers),
      correctAnswers: sanitizeForFirestore(correctAnswers),
      score,
      correctCount,
      totalQuestions: correctAnswers.length,
      completedAt: serverTimestamp(),
      ...(options.assignmentId != null && { assignmentId: options.assignmentId }),
      ...(liveSessionId != null && { liveSessionId }),
      ...(options.assignmentTeacherId != null && { teacherId: options.assignmentTeacherId }),
      ...(options.assignmentTeacherClassId != null && { teacherClassId: options.assignmentTeacherClassId }),
      ...(options.authorId != null && { authorId: options.authorId }),
      ...(options.authorName != null && { authorName: options.authorName }),
      ...(meta.composer != null && { composer: meta.composer }),
      ...(meta.difficulty != null && { difficulty: meta.difficulty }),
      ...(Array.isArray(meta.autoTags) && { autoTags: sanitizeForFirestore(meta.autoTags) }),
      ...(meta.sourceNodeId != null && { sourceNodeId: meta.sourceNodeId }),
      ...(meta.section != null && { section: meta.section }),
      ...(meta.musicCategory != null && { musicCategory: meta.musicCategory })
    }
    
    const docRef = await addDoc(collection(db, 'attempts'), attemptData)
    await recordAttemptStats({
      score,
      teacherClassId: options.assignmentTeacherClassId || null,
      completedAt: new Date()
    })
    
    // Incrémenter l'XP (déjà calculée selon level par marqueur)
    if (xpGained > 0) {
      await incrementUserXP(userId, xpGained)
    }
    
    return {
      id: docRef.id,
      ...attemptData,
      xpGained,
      ...(noXpReason != null && { noXpReason }),
      ...(priorBestScore != null && { priorBestScore })
    }
  } catch (error) {
    console.error('Erreur lors de la sauvegarde de la tentative:', error)
    throw error
  }
}

/**
 * Récupère les tentatives liées à une session live et à un exercice.
 */
export async function getAttemptsByLiveSessionExercise(liveSessionId, exerciseId, maxCount = 300) {
  try {
    if (!liveSessionId || !exerciseId) return []
    const q = query(
      collection(db, 'attempts'),
      where('liveSessionId', '==', liveSessionId),
      limit(Math.max(400, Number(maxCount) > 0 ? Number(maxCount) * 2 : 600))
    )
    const snapshot = await getDocs(q)
    const attempts = []
    snapshot.forEach((docSnap) => {
      const data = {
        id: docSnap.id,
        ...docSnap.data()
      }
      if (data.exerciseId === exerciseId) attempts.push(data)
    })
    return attempts
      .sort((a, b) => {
        const ta = a.completedAt?.toDate?.()?.getTime?.() ?? 0
        const tb = b.completedAt?.toDate?.()?.getTime?.() ?? 0
        return tb - ta
      })
      .slice(0, Number(maxCount) > 0 ? Number(maxCount) : 300)
  } catch (error) {
    console.error('Erreur lors de la récupération des tentatives live:', error)
    throw error
  }
}

/**
 * Récupère la dernière tentative d'un élève pour un exercice de session live.
 */
export async function getLatestUserAttemptByLiveSessionExercise(userId, liveSessionId, exerciseId) {
  try {
    if (!userId || !liveSessionId || !exerciseId) return null
    const q = query(
      collection(db, 'attempts'),
      where('userId', '==', userId),
      where('liveSessionId', '==', liveSessionId),
      where('exerciseId', '==', exerciseId),
      limit(3)
    )
    const snapshot = await getDocs(q)
    if (snapshot.empty) return null
    const attempts = []
    snapshot.forEach((docSnap) => {
      attempts.push({
        id: docSnap.id,
        ...docSnap.data()
      })
    })
    attempts.sort((a, b) => {
      const ta = a.completedAt?.toDate?.()?.getTime?.() ?? 0
      const tb = b.completedAt?.toDate?.()?.getTime?.() ?? 0
      return tb - ta
    })
    return attempts[0] || null
  } catch (error) {
    console.error('Erreur lors de la récupération de la tentative live élève:', error)
    throw error
  }
}

/**
 * Récupère toutes les tentatives d'une session live en une seule lecture.
 * Optionnellement filtre aux exerciseIds fournis.
 */
export async function getAttemptsByLiveSession(liveSessionId, options = {}) {
  try {
    if (!liveSessionId) return []
    const {
      exerciseIds = null,
      maxCount = 1500
    } = options
    const allowed = Array.isArray(exerciseIds) && exerciseIds.length > 0
      ? new Set(exerciseIds.map((id) => String(id)))
      : null
    const q = query(
      collection(db, 'attempts'),
      where('liveSessionId', '==', liveSessionId),
      limit(Math.max(400, Number(maxCount) > 0 ? Number(maxCount) : 1500))
    )
    const snapshot = await getDocs(q)
    const attempts = []
    snapshot.forEach((docSnap) => {
      const data = {
        id: docSnap.id,
        ...docSnap.data()
      }
      if (!allowed || allowed.has(String(data.exerciseId || ''))) {
        attempts.push(data)
      }
    })
    return attempts.sort((a, b) => {
      const ta = a.completedAt?.toDate?.()?.getTime?.() ?? 0
      const tb = b.completedAt?.toDate?.()?.getTime?.() ?? 0
      return tb - ta
    })
  } catch (error) {
    console.error('Erreur lors de la récupération des tentatives live (session):', error)
    throw error
  }
}

/**
 * Récupère les tentatives liées à un devoir.
 * @param {string} assignmentId
 * @param {number} maxCount
 */
export async function getAttemptsByAssignment(assignmentId, maxCount = 300) {
  try {
    if (!assignmentId) return []
    const q = query(
      collection(db, 'attempts'),
      where('assignmentId', '==', assignmentId),
      orderBy('completedAt', 'desc'),
      limit(maxCount)
    )
    const snapshot = await getDocs(q)
    const attempts = []
    snapshot.forEach((docSnap) => {
      attempts.push({
        id: docSnap.id,
        ...docSnap.data()
      })
    })
    return attempts
  } catch (error) {
    console.error('Erreur lors de la récupération des tentatives par devoir:', error)
    throw error
  }
}

/**
 * Récupère l'historique des tentatives d'un utilisateur
 */
export async function getUserAttempts(userId, limitCount = 10) {
  try {
    // Récupérer toutes les tentatives de l'utilisateur (sans orderBy pour éviter l'index)
    const q = query(
      collection(db, 'attempts'),
      where('userId', '==', userId)
    )
    
    const querySnapshot = await getDocs(q)
    const attempts = []
    
    querySnapshot.forEach((doc) => {
      attempts.push({
        id: doc.id,
        ...doc.data()
      })
    })
    
    // Trier côté client par date (plus récent en premier)
    attempts.sort((a, b) => {
      const dateA = a.completedAt?.toDate?.() || new Date(a.completedAt || 0)
      const dateB = b.completedAt?.toDate?.() || new Date(b.completedAt || 0)
      return dateB - dateA
    })
    
    // Limiter le nombre de résultats
    return attempts.slice(0, limitCount)
  } catch (error) {
    console.error('Erreur lors de la récupération des tentatives:', error)
    throw error
  }
}

/**
 * Récupère toutes les tentatives d'un utilisateur (pour les stats)
 */
export async function getAllUserAttempts(userId) {
  try {
    const q = query(
      collection(db, 'attempts'),
      where('userId', '==', userId)
    )
    
    const querySnapshot = await getDocs(q)
    const attempts = []
    
    querySnapshot.forEach((doc) => {
      attempts.push({
        id: doc.id,
        ...doc.data()
      })
    })
    
    return attempts
  } catch (error) {
    console.error('Erreur lors de la récupération de toutes les tentatives:', error)
    throw error
  }
}

/**
 * Récupère la dernière tentative d'un utilisateur (pour afficher "dernière activité")
 * Nécessite un index Firestore : collection attempts, userId (ASC), completedAt (DESC)
 */
export async function getLastAttemptForUser(userId) {
  try {
    const q = query(
      collection(db, 'attempts'),
      where('userId', '==', userId),
      orderBy('completedAt', 'desc'),
      limit(1)
    )
    const snapshot = await getDocs(q)
    if (snapshot.empty) return null
    const d = snapshot.docs[0]
    return { id: d.id, ...d.data() }
  } catch (error) {
    console.warn('getLastAttemptForUser:', error)
    return null
  }
}

/**
 * Retourne le nombre de tentatives réalisées sur les exercices d'un auteur (prof).
 * Nécessite que les tentatives aient le champ authorId (dénormalisation).
 */
export async function getAttemptsCountByAuthor(authorId) {
  try {
    const q = query(
      collection(db, 'attempts'),
      where('authorId', '==', authorId)
    )
    const snapshot = await getCountFromServer(q)
    return snapshot.data().count ?? 0
  } catch (error) {
    console.error('Erreur lors du comptage des tentatives par auteur:', error)
    throw error
  }
}

/**
 * Récupère les tentatives d'un exercice spécifique
 */
export async function getExerciseAttempts(exerciseId) {
  try {
    // Récupérer toutes les tentatives de l'exercice (sans orderBy pour éviter l'index)
    const q = query(
      collection(db, 'attempts'),
      where('exerciseId', '==', exerciseId)
    )
    
    const querySnapshot = await getDocs(q)
    const attempts = []
    
    querySnapshot.forEach((doc) => {
      attempts.push({
        id: doc.id,
        ...doc.data()
      })
    })
    
    // Trier côté client par date (plus récent en premier)
    attempts.sort((a, b) => {
      const dateA = a.completedAt?.toDate?.() || new Date(a.completedAt || 0)
      const dateB = b.completedAt?.toDate?.() || new Date(b.completedAt || 0)
      return dateB - dateA
    })
    
    return attempts
  } catch (error) {
    console.error('Erreur lors de la récupération des tentatives:', error)
    throw error
  }
}

function normalizeDateInput(input, fallback) {
  if (!input) return fallback
  if (input.toDate) return input.toDate()
  const parsed = new Date(input)
  if (Number.isNaN(parsed.getTime())) return fallback
  return parsed
}

function getAttemptCompletedAtDate(attempt) {
  const raw = attempt?.completedAt
  if (raw?.toDate) return raw.toDate()
  const parsed = new Date(raw || 0)
  if (Number.isNaN(parsed.getTime())) return new Date(0)
  return parsed
}

function answerToLabel(answer) {
  if (answer == null) return 'Non répondu'
  if (typeof answer === 'string') {
    const trimmed = answer.trim()
    return trimmed || 'Non répondu'
  }
  if (typeof answer === 'object') {
    const unified = userAnswerToLabel(answer)
    if (unified && unified !== 'Non répondu') return unified
    const detailed = formatChordDetailed(answer)
    if (detailed && detailed !== 'Non répondu') return detailed
    if (typeof answer.degree === 'string' && answer.degree.trim()) {
      const degree = answer.degree.trim()
      const cadence = typeof answer.cadence === 'string' ? answer.cadence.trim() : ''
      return cadence ? `${degree} (${cadence})` : degree
    }
  }
  return 'Non répondu'
}

function toSafeScore(score) {
  const value = Number(score)
  if (!Number.isFinite(value)) return 0
  return Math.max(0, Math.min(100, value))
}

/**
 * Statistiques détaillées d'un exercice, côté professeur.
 * Requête bornée pour limiter quota/latence.
 */
export async function getExerciseStatsForTeacher(teacherId, exerciseId, options = {}) {
  try {
    if (!teacherId || !exerciseId) return null
    const {
      teacherClassId = null,
      startDate = null,
      endDate = null,
      recentStudentsLimit = 20,
      attemptsCap = 300,
      successThreshold = 70
    } = options

    const now = new Date()
    const defaultStart = new Date(now)
    defaultStart.setDate(defaultStart.getDate() - 90)
    const startAt = normalizeDateInput(startDate, defaultStart)
    const endAt = normalizeDateInput(endDate, now)
    const safeCap = Math.max(50, Math.min(1000, Number(attemptsCap) || 300))
    const safeRecentLimit = Math.max(1, Math.min(50, Number(recentStudentsLimit) || 20))
    const safeSuccessThreshold = Math.max(1, Math.min(100, Number(successThreshold) || 70))

    // Quota strict: une seule lecture bornée au cap demandé.
    // On récupère les dernières tentatives de l'exercice, puis filtre côté client (périmètre prof + dates + classe).
    const q = query(
      collection(db, 'attempts'),
      where('exerciseId', '==', exerciseId),
      orderBy('completedAt', 'desc'),
      limit(safeCap)
    )
    const snapshot = await getDocs(q)
    const attempts = []
    snapshot.forEach((docSnap) => {
      const data = { id: docSnap.id, ...docSnap.data() }
      const completedDate = getAttemptCompletedAtDate(data)
      if (completedDate < startAt || completedDate > endAt) return
      if (teacherClassId && data.teacherClassId !== teacherClassId) return
      const matchesTeacher =
        data.teacherId === teacherId ||
        data.authorId === teacherId ||
        (!data.teacherId && !data.authorId)
      if (!matchesTeacher) return
      attempts.push(data)
    })

    const totalAttempts = attempts.length
    if (totalAttempts === 0) {
      return {
        exerciseId,
        totalAttempts: 0,
        uniqueStudents: 0,
        averageScore: 0,
        medianScore: 0,
        successRate: 0,
        successThreshold: safeSuccessThreshold,
        attemptsCap: safeCap,
        recentStudents: [],
        topErrors: [],
        scoreDistribution: { low: 0, mid: 0, good: 0, excellent: 0 }
      }
    }

    const scores = attempts.map((a) => toSafeScore(a.score)).sort((a, b) => a - b)
    const sumScores = scores.reduce((acc, n) => acc + n, 0)
    const averageScore = Math.round((sumScores / totalAttempts) * 10) / 10
    const middle = Math.floor(scores.length / 2)
    const medianScore = scores.length % 2 === 0
      ? Math.round(((scores[middle - 1] + scores[middle]) / 2) * 10) / 10
      : scores[middle]
    const successCount = attempts.reduce((acc, a) => acc + (toSafeScore(a.score) >= safeSuccessThreshold ? 1 : 0), 0)
    const successRate = Math.round((successCount / totalAttempts) * 100)

    const scoreDistribution = attempts.reduce((acc, a) => {
      const score = toSafeScore(a.score)
      if (score < 40) acc.low += 1
      else if (score < 70) acc.mid += 1
      else if (score < 85) acc.good += 1
      else acc.excellent += 1
      return acc
    }, { low: 0, mid: 0, good: 0, excellent: 0 })

    const userAggregates = new Map()
    attempts.forEach((attempt) => {
      const userId = String(attempt.userId || '')
      if (!userId) return
      const score = toSafeScore(attempt.score)
      const completedAt = getAttemptCompletedAtDate(attempt)
      const current = userAggregates.get(userId) || {
        userId,
        attemptCount: 0,
        successCount: 0,
        bestScore: 0,
        totalScore: 0,
        lastAttemptAt: null,
        lastScore: 0
      }
      current.attemptCount += 1
      current.totalScore += score
      current.bestScore = Math.max(current.bestScore, score)
      if (score >= safeSuccessThreshold) current.successCount += 1
      if (!current.lastAttemptAt || completedAt > current.lastAttemptAt) {
        current.lastAttemptAt = completedAt
        current.lastScore = score
      }
      userAggregates.set(userId, current)
    })

    const sortedByDate = [...attempts].sort((a, b) => getAttemptCompletedAtDate(b) - getAttemptCompletedAtDate(a))
    const recentUserIds = []
    const seen = new Set()
    sortedByDate.forEach((attempt) => {
      const userId = String(attempt.userId || '')
      if (!userId || seen.has(userId)) return
      seen.add(userId)
      recentUserIds.push(userId)
    })
    const recentTargetUserIds = recentUserIds.slice(0, safeRecentLimit)
    const users = await Promise.all(recentTargetUserIds.map(async (userId) => {
      try {
        return await getUserById(userId)
      } catch {
        return null
      }
    }))
    const userNamesById = new Map()
    users.forEach((u, index) => {
      const userId = recentTargetUserIds[index]
      const fallback = `Élève ${index + 1}`
      userNamesById.set(userId, u?.displayName || u?.email || fallback)
    })
    const recentStudents = recentTargetUserIds.map((userId) => {
      const agg = userAggregates.get(userId)
      if (!agg) return null
      const average = agg.attemptCount > 0 ? Math.round((agg.totalScore / agg.attemptCount) * 10) / 10 : 0
      return {
        userId,
        displayName: userNamesById.get(userId) || 'Élève',
        attemptCount: agg.attemptCount,
        successRate: Math.round((agg.successCount / Math.max(1, agg.attemptCount)) * 100),
        averageScore: average,
        bestScore: agg.bestScore,
        lastScore: agg.lastScore,
        lastAttemptAt: agg.lastAttemptAt
      }
    }).filter(Boolean)

    const questionStats = new Map()
    attempts.forEach((attempt) => {
      const userAnswers = Array.isArray(attempt.userAnswers) ? attempt.userAnswers : []
      const correctAnswers = Array.isArray(attempt.correctAnswers) ? attempt.correctAnswers : []
      const maxLen = Math.max(userAnswers.length, correctAnswers.length)
      for (let i = 0; i < maxLen; i += 1) {
        const current = questionStats.get(i) || {
          questionIndex: i + 1,
          answeredCount: 0,
          wrongCounts: new Map(),
          correctLabel: ''
        }
        const correctLabel = answerToLabel(correctAnswers[i])
        if (!current.correctLabel && correctLabel && correctLabel !== 'Non répondu') {
          current.correctLabel = correctLabel
        }
        const answerLabel = answerToLabel(userAnswers[i])
        const expectedLabel = (correctLabel && correctLabel !== 'Non répondu')
          ? correctLabel
          : current.correctLabel
        if (answerLabel !== 'Non répondu') current.answeredCount += 1
        if (answerLabel && answerLabel !== 'Non répondu' && answerLabel !== expectedLabel) {
          current.wrongCounts.set(answerLabel, (current.wrongCounts.get(answerLabel) || 0) + 1)
        }
        questionStats.set(i, current)
      }
    })

    const topErrors = []
    questionStats.forEach((entry) => {
      const wrongItems = Array.from(entry.wrongCounts.entries())
        .map(([label, count]) => ({ label, count }))
        .sort((a, b) => b.count - a.count)
      wrongItems.slice(0, 3).forEach((item) => {
        topErrors.push({
          questionIndex: entry.questionIndex,
          label: item.label,
          count: item.count,
          percent: Math.round((item.count / Math.max(1, totalAttempts)) * 100),
          responseRate: Math.round((entry.answeredCount / Math.max(1, totalAttempts)) * 100),
          correctLabel: entry.correctLabel || 'Non répondu'
        })
      })
    })
    topErrors.sort((a, b) => b.count - a.count)

    return {
      exerciseId,
      totalAttempts,
      uniqueStudents: userAggregates.size,
      averageScore,
      medianScore,
      successRate,
      successThreshold: safeSuccessThreshold,
      attemptsCap: safeCap,
      sampledAttempts: totalAttempts,
      scoreDistribution,
      recentStudents,
      topErrors: topErrors.slice(0, 12)
    }
  } catch (error) {
    console.error('Erreur lors de la récupération des stats exercice (prof):', error)
    throw error
  }
}

/**
 * Récupère les tentatives d'un professeur sur une période (option classe).
 * Fusionne : tentatives avec teacherId (ex. devoirs) + tentatives par teacherClassId
 * (sinon la pratique sans devoir n'a souvent pas teacherId et n'apparaissait pas dans les insights).
 */
export async function getAttemptsByTeacherInRange(teacherId, options = {}) {
  try {
    if (!teacherId) return []
    const {
      teacherClassId = null,
      rosterClassIds = [],
      startDate = null,
      endDate = null,
      maxCount = 2500
    } = options

    const now = new Date()
    const defaultStart = new Date(now)
    defaultStart.setDate(defaultStart.getDate() - 30)
    const startAt = normalizeDateInput(startDate, defaultStart)
    const endAt = normalizeDateInput(endDate, now)

    const classIds = teacherClassId
      ? [teacherClassId]
      : [...new Set((rosterClassIds || []).filter(Boolean))]

    const queryPlans = [
      { key: 'byTeacher', constraints: [where('teacherId', '==', teacherId)] }
    ]
    classIds.forEach((cid) => {
      queryPlans.push({
        key: `class:${cid}`,
        constraints: [where('teacherClassId', '==', cid)]
      })
    })

    const numQueries = queryPlans.length
    const perQueryLimit = Math.min(2000, Math.max(80, Math.ceil(maxCount / Math.max(1, numQueries))))

    const snapshots = await Promise.all(
      queryPlans.map(({ constraints }) =>
        getDocs(
          query(
            collection(db, 'attempts'),
            ...constraints,
            orderBy('completedAt', 'desc'),
            limit(perQueryLimit)
          )
        )
      )
    )

    const byId = new Map()
    snapshots.forEach((snapshot) => {
      snapshot.forEach((docSnap) => {
        if (byId.has(docSnap.id)) return
        const data = { id: docSnap.id, ...docSnap.data() }
        const completedDate = getAttemptCompletedAtDate(data)
        if (completedDate >= startAt && completedDate <= endAt) {
          byId.set(docSnap.id, data)
        }
      })
    })

    const attempts = Array.from(byId.values())
    attempts.sort((a, b) => getAttemptCompletedAtDate(b) - getAttemptCompletedAtDate(a))
    return attempts.slice(0, maxCount)
  } catch (error) {
    console.error('Erreur lors de la récupération des tentatives du professeur:', error)
    throw error
  }
}

/**
 * Récupère les tentatives d'un utilisateur sur une période limitée.
 */
export async function getUserAttemptsInRange(userId, options = {}) {
  try {
    if (!userId) return []
    const {
      startDate = null,
      endDate = null,
      maxCount = 250
    } = options
    const now = new Date()
    const defaultStart = new Date(now)
    defaultStart.setDate(defaultStart.getDate() - 30)
    const startAt = normalizeDateInput(startDate, defaultStart)
    const endAt = normalizeDateInput(endDate, now)

    const q = query(
      collection(db, 'attempts'),
      where('userId', '==', userId),
      orderBy('completedAt', 'desc'),
      limit(maxCount)
    )
    const snapshot = await getDocs(q)
    const attempts = []
    snapshot.forEach((docSnap) => {
      const data = docSnap.data()
      const completedDate = data.completedAt?.toDate ? data.completedAt.toDate() : new Date(data.completedAt || 0)
      if (completedDate >= startAt && completedDate <= endAt) {
        attempts.push({
          id: docSnap.id,
          ...data
        })
      }
    })
    return attempts
  } catch (error) {
    console.error('Erreur lors de la récupération des tentatives utilisateur (période):', error)
    throw error
  }
}

